import { applicationName } from "./utils/applicationName.es.js";
import { deepEquals } from "./utils/deepEquals.es.js";
import { eventProducerUrl } from "./utils/eventProducerUrl.es.js";
import { getMerkleIds } from "./browser/plugins/merkle/getMerkleIds.es.js";
import { logger } from "./utils/logger.es.js";
import { v as v4 } from "./v4-88c20f3b.js";
import { g as getCookieHandler } from "./cookieHandler-ac6954c1.js";
const EH_EVENT_TIME = "eh_event_time";
const EH_PAGE_VIEW_ID = "eh_page_view_id";
const EH_PREVIOUS_PAGE_VIEW_ID = "eh_previous_page_view_id";
const EH_PAGE_NAME = "eh_page_name";
const PAGE_VIEW_ID = "page_view_id";
const TIME_SINCE_LAST_EVENT = "time_since_last_event";
const getSessionStorageData = (key) => window.sessionStorage.getItem(key);
const setSessionStorageData = (key, value) => window.sessionStorage.setItem(key, value);
const timeOnPage = (eventType, pageName) => {
  if (!eventType || !pageName) {
    return { [PAGE_VIEW_ID]: void 0, [TIME_SINCE_LAST_EVENT]: void 0 };
  }
  const eventTime = new Date(getSessionStorageData(EH_EVENT_TIME)) || null;
  const currentPageName = getSessionStorageData(EH_PAGE_NAME) || null;
  const pageId = getSessionStorageData(EH_PAGE_VIEW_ID) || null;
  let timeDifference;
  if (eventType === "page") {
    if (!pageId) {
      const newPageViewId = v4();
      setSessionStorageData(EH_EVENT_TIME, /* @__PURE__ */ new Date());
      setSessionStorageData(EH_PAGE_VIEW_ID, newPageViewId);
      setSessionStorageData(EH_PAGE_NAME, pageName);
      return {
        [PAGE_VIEW_ID]: newPageViewId,
        [TIME_SINCE_LAST_EVENT]: 0
      };
    } else {
      const currentTime = /* @__PURE__ */ new Date();
      timeDifference = timeCalculation(currentTime, eventTime);
      setSessionStorageData(EH_EVENT_TIME, currentTime);
      setSessionStorageData(EH_PAGE_NAME, pageName);
      if (pageName !== currentPageName) {
        setSessionStorageData(EH_PAGE_VIEW_ID, v4());
        setSessionStorageData(EH_PREVIOUS_PAGE_VIEW_ID, pageId);
      }
      return {
        [PAGE_VIEW_ID]: pageId,
        [TIME_SINCE_LAST_EVENT]: timeDifference
      };
    }
  } else {
    if (pageName !== currentPageName) {
      const newPageId = v4();
      setSessionStorageData(EH_PAGE_NAME, pageName);
      setSessionStorageData(EH_PAGE_VIEW_ID, newPageId);
      const currentTime2 = /* @__PURE__ */ new Date();
      timeDifference = timeCalculation(currentTime2, eventTime);
      return {
        [PAGE_VIEW_ID]: newPageId,
        [TIME_SINCE_LAST_EVENT]: timeDifference
      };
    }
    const currentTime = /* @__PURE__ */ new Date();
    timeDifference = timeCalculation(currentTime, eventTime);
    setSessionStorageData(EH_EVENT_TIME, currentTime);
    return {
      [PAGE_VIEW_ID]: pageId,
      [TIME_SINCE_LAST_EVENT]: timeDifference
    };
  }
};
const timeCalculation = (currentTime, eventTime) => {
  if (isNaN(currentTime.getTime()) || isNaN(eventTime.getTime())) {
    return void 0;
  }
  return parseInt(
    (Math.abs(currentTime.getTime() - eventTime.getTime()) / 1e3).toFixed(1),
    10
  );
};
const VISIT_ID = "visit_id";
const EH_VISIT_ID = "eh_visit_id";
const EH_VISIT_TS = "eh_visit_ts";
const URL_VISIT_IDS = "url_visit_ids";
const EH_PREVIOUS_VISIT_ID = "eh_previous_visit_id";
const timeThreshold = 30 * 60;
const longCookieExpiration = new Date(
  Date.now() + 1e3 * 60 * 60 * 24 * 365 * 10
);
const timeExpiredCheck = (timeDifference, visitId, cookieOptions, cookieHandler) => {
  if (timeDifference > timeThreshold) {
    const newVisitId = v4();
    cookieHandler.setCookie(
      {
        [EH_PREVIOUS_VISIT_ID]: visitId,
        [EH_VISIT_ID]: newVisitId
      },
      [{ ...cookieOptions, expires: longCookieExpiration }, cookieOptions]
    );
    return newVisitId;
  } else {
    return visitId;
  }
};
const processUrlVisitId = (cookies, urlVisitId, cookieOptions, cookieHandler) => {
  try {
    const currentTime = /* @__PURE__ */ new Date();
    const urlVisitIdsArr = JSON.parse(cookies[URL_VISIT_IDS] || null) || [];
    if (urlVisitIdsArr.includes(urlVisitId)) {
      const ehVisitIdSetTime = new Date(cookies[EH_VISIT_TS]) || null;
      const timeDifference = timeCalculation(currentTime, ehVisitIdSetTime);
      cookieHandler.setCookie({ [EH_VISIT_TS]: /* @__PURE__ */ new Date() }, [cookieOptions]);
      if (urlVisitId === cookies[URL_VISIT_IDS][0]) {
        return timeExpiredCheck(
          timeDifference,
          urlVisitId,
          cookieOptions,
          cookieHandler
        );
      } else {
        const visitId = cookies[EH_VISIT_ID];
        return timeExpiredCheck(
          timeDifference,
          visitId,
          cookieOptions,
          cookieHandler
        );
      }
    } else {
      urlVisitIdsArr.unshift(urlVisitId);
      cookieHandler.setCookie(
        {
          [EH_VISIT_ID]: urlVisitId,
          [EH_VISIT_TS]: currentTime,
          [URL_VISIT_IDS]: JSON.stringify(urlVisitIdsArr)
        },
        [cookieOptions, cookieOptions, cookieOptions]
      );
      return urlVisitId;
    }
  } catch (error) {
    logger.error(`Error parsing URL Paramater ${error}`);
  }
};
const getUrlVisitId = () => {
  let urlVisitId = null;
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    urlVisitId = urlParams.get(VISIT_ID);
    if (urlVisitId) {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(urlVisitId)) {
        logger.error("Invalid URL VisitId format");
        urlVisitId = null;
      }
    }
    if (urlVisitId === "undefined") {
      logger.error("URL VisitId is `undefined`");
      urlVisitId = null;
    }
  }
  return urlVisitId;
};
const getVisitId = (adapter) => {
  try {
    const cookieHandler = getCookieHandler(adapter);
    const urlVisitId = getUrlVisitId();
    const cookieOptions = cookieHandler.getCookieOptions();
    const cookies = cookieHandler.parseCookies();
    const visitId = cookies[EH_VISIT_ID] || null;
    if (urlVisitId) {
      return processUrlVisitId(
        cookies,
        urlVisitId,
        cookieOptions,
        cookieHandler
      );
    } else if (visitId) {
      const currentTime = /* @__PURE__ */ new Date();
      const visitIdSetTime = new Date(cookies[EH_VISIT_TS]) || null;
      const timeDifference = timeCalculation(currentTime, visitIdSetTime);
      cookieHandler.setCookie({ [EH_VISIT_TS]: /* @__PURE__ */ new Date() }, [cookieOptions]);
      return timeExpiredCheck(
        timeDifference,
        visitId,
        cookieOptions,
        cookieHandler
      );
    } else {
      const firstVisitId = v4();
      cookieHandler.setCookie(
        {
          [EH_VISIT_ID]: firstVisitId,
          [EH_VISIT_TS]: /* @__PURE__ */ new Date()
        },
        [cookieOptions, cookieOptions]
      );
      return firstVisitId;
    }
  } catch (error) {
    logger.error("Error getting Visit Id", error);
  }
};
const merkleTimeCalculation = Date.now();
let merkleDataTracker;
const initializeMerkleListener = () => {
  if (typeof window === "undefined") {
    return;
  }
  window.addEventListener("_svdatalayer_ready", () => {
    const timeDifference = Date.now() - merkleTimeCalculation;
    logger.info(
      `MerkleTime: ${timeDifference}ms (${(timeDifference / 1e3).toFixed(2)}s)`
    );
    logger.info("Merkle event _svdatalayer_ready received");
    fireMerkleEvent();
  });
};
const fireMerkleEvent = async () => {
  const merkleData = await getMerkleIds();
  const isEqual = deepEquals(merkleDataTracker, merkleData);
  if (!isEqual) {
    merkleDataTracker = merkleData;
    const payload = {
      event_origin: "client",
      event_id: v4(),
      application: applicationName,
      event_datetime: Date.now(),
      event_type: "track",
      event_action: "merkle_event_fired",
      event_name: "merkle_datalayer_ready",
      ids: {
        visit_id: getVisitId(),
        ...merkleData
      }
    };
    const requestOptions = {
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
      method: "POST"
    };
    try {
      fetch(eventProducerUrl, requestOptions);
    } catch (e) {
      logger.error("analytics-merkleId: track event failed");
    }
  }
};
const analyticsMerkleIds = () => {
  initializeMerkleListener();
  return {
    name: "analytics-merkleId",
    page: async ({ payload }) => {
      try {
        const ids = payload.properties.ids || {};
        const merkleData = await getMerkleIds();
        const allIds = { ...ids, ...merkleData ?? {} };
        payload.properties = {
          ...payload.properties,
          ids: allIds
        };
      } catch (error) {
        logger.error("Failed to process merkle IDs:", error);
      }
    },
    track: async ({ payload }) => {
      try {
        const ids = payload.properties.ids || {};
        const merkleData = await getMerkleIds();
        const allIds = { ...ids, ...merkleData ?? {} };
        payload.properties = {
          ...payload.properties,
          ids: allIds
        };
      } catch (error) {
        logger.error("Failed to process merkle IDs:", error);
      }
    }
  };
};
export {
  analyticsMerkleIds as a,
  getVisitId as g,
  timeOnPage as t
};
